import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell, Webhook, MessageSquare, Send, CheckCircle, XCircle, 
  AlertTriangle, Users, FileText, Image, Flag, BookOpen, 
  Award, RefreshCw, ArrowLeft, TestTube, Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../App';

const API = '/api';

// Event type configuration
const EVENT_CONFIGS = {
  user_signup: {
    label: 'New User Signup',
    description: 'When a new user registers on the platform',
    icon: Users,
    color: 'text-green-400',
    severity: 'info',
    recommended: true
  },
  content_reported: {
    label: 'Content Reported',
    description: 'When content is flagged by users (always recommended)',
    icon: Flag,
    color: 'text-red-400',
    severity: 'critical',
    recommended: true
  },
  chapter_published: {
    label: 'Chapter Published',
    description: 'When a new comic chapter is published',
    icon: FileText,
    color: 'text-blue-400',
    severity: 'low',
    recommended: false
  },
  comic_created: {
    label: 'New Comic Series',
    description: 'When a creator starts a new comic series',
    icon: BookOpen,
    color: 'text-purple-400',
    severity: 'low',
    recommended: false
  },
  artwork_uploaded: {
    label: 'Artwork Uploaded',
    description: 'When new artwork is uploaded to the gallery',
    icon: Image,
    color: 'text-pink-400',
    severity: 'low',
    recommended: false
  },
  novel_created: {
    label: 'New Novel',
    description: 'When a creator starts a new novel',
    icon: BookOpen,
    color: 'text-orange-400',
    severity: 'low',
    recommended: false
  },
  novel_chapter_published: {
    label: 'Novel Chapter Published',
    description: 'When a new novel chapter is published',
    icon: FileText,
    color: 'text-cyan-400',
    severity: 'low',
    recommended: false
  },
  creator_verified: {
    label: 'Creator Verified',
    description: 'When a creator completes verification',
    icon: Award,
    color: 'text-yellow-400',
    severity: 'info',
    recommended: true
  }
};

const AdminAlertSettings = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [enabledEvents, setEnabledEvents] = useState({});

  const loadSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/alert-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setEnabledEvents(response.data.enabled_events || {});
    } catch (error) {
      console.error('Failed to load alert settings:', error);
      toast.error('Failed to load alert settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || (user?.role !== 'admin' && !user?.is_admin)) {
      navigate('/');
      return;
    }
    
    loadSettings();
  }, [user, navigate, authLoading, loadSettings]);

  const handleToggleEvent = (eventType) => {
    setEnabledEvents(prev => ({
      ...prev,
      [eventType]: !prev[eventType]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/alert-settings`, {
        enabled_events: enabledEvents
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alert settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save alert settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/alert-settings/test-webhook`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test message sent to Discord!');
    } catch (error) {
      console.error('Failed to test webhook:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  const hasChanges = JSON.stringify(enabledEvents) !== JSON.stringify(settings?.enabled_events || {});

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-alert-settings">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Bell className="w-8 h-8" />
                Admin Alert Channels
              </h1>
              <p className="text-white/80 mt-2">
                Configure how you receive alerts for important platform events
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Discord Webhook Status */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Webhook className="w-5 h-5 text-indigo-400" />
              Discord Webhook
            </CardTitle>
            <CardDescription>
              Send real-time alerts to a Discord channel
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                {settings?.discord_webhook_configured ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-400" />
                    <div>
                      <p className="font-medium text-green-400">Webhook Configured</p>
                      <p className="text-sm text-zinc-400">
                        Discord alerts are ready to send
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <XCircle className="w-6 h-6 text-red-400" />
                    <div>
                      <p className="font-medium text-red-400">Not Configured</p>
                      <p className="text-sm text-zinc-400">
                        Set ADMIN_DISCORD_WEBHOOK_URL in environment variables
                      </p>
                    </div>
                  </>
                )}
              </div>
              
              {settings?.discord_webhook_configured && (
                <Button
                  onClick={handleTestWebhook}
                  disabled={testing}
                  variant="outline"
                  className="border-indigo-500 text-indigo-400 hover:bg-indigo-500/10"
                  data-testid="test-webhook-btn"
                >
                  {testing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Test Webhook
                </Button>
              )}
            </div>

            {!settings?.discord_webhook_configured && (
              <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700">
                <h4 className="font-medium text-white mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  How to set up Discord Webhook
                </h4>
                <ol className="text-sm text-zinc-400 space-y-2 list-decimal list-inside">
                  <li>Go to your Discord server settings → Integrations → Webhooks</li>
                  <li>Click &quot;New Webhook&quot; and choose a channel</li>
                  <li>Copy the webhook URL</li>
                  <li>Add <code className="bg-zinc-700 px-1 rounded">ADMIN_DISCORD_WEBHOOK_URL=your_url</code> to backend/.env</li>
                  <li>Restart the backend service</li>
                </ol>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Event Configuration */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                  Alert Events
                </CardTitle>
                <CardDescription>
                  Choose which events trigger Discord alerts
                </CardDescription>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                data-testid="save-alert-settings-btn"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(EVENT_CONFIGS).map(([eventType, config]) => {
                const Icon = config.icon;
                const isEnabled = enabledEvents[eventType] ?? config.recommended;
                
                return (
                  <div 
                    key={eventType}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      isEnabled 
                        ? 'bg-zinc-800/50 border-zinc-700' 
                        : 'bg-zinc-900/50 border-zinc-800'
                    }`}
                    data-testid={`event-toggle-${eventType}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg bg-zinc-800 ${config.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-white">{config.label}</h4>
                          {config.severity === 'critical' && (
                            <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/50 text-xs">
                              Critical
                            </Badge>
                          )}
                          {config.recommended && (
                            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/50 text-xs">
                              Recommended
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400">{config.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => handleToggleEvent(eventType)}
                      disabled={!settings?.discord_webhook_configured}
                    />
                  </div>
                );
              })}
            </div>
            
            {/* Rate Limit Info */}
            <div className="mt-6 p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-white">Rate Limiting</h4>
                  <p className="text-sm text-zinc-400 mt-1">
                    User signup alerts are rate-limited to <strong className="text-white">{settings?.rate_limits?.user_signup || 10} per hour</strong> to prevent spam during high-traffic periods. Reports are always sent immediately.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Future Channels Preview */}
        <Card className="bg-zinc-900 border-zinc-800 opacity-60">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-zinc-500" />
              Coming Soon
            </CardTitle>
            <CardDescription>
              Additional alert channels planned for future releases
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
                <h4 className="font-medium text-zinc-400 mb-1">📧 Email Alerts (P1)</h4>
                <p className="text-sm text-zinc-500">Real email notifications via SendGrid/Postmark</p>
              </div>
              <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
                <h4 className="font-medium text-zinc-400 mb-1">📱 SMS Alerts (P1)</h4>
                <p className="text-sm text-zinc-500">Critical alerts via Twilio SMS</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAlertSettings;
